// FacturationModal.jsx
import React, { useState, useEffect } from 'react';
import { 
  CreditCard, 
  Calendar, 
  Download, 
  CheckCircle, 
  Clock, 
  AlertCircle, 
  RefreshCw, 
  ChevronDown, 
  FileText, 
  X, 
  Check,
  Info,
  Smartphone,
  Star,
  ArrowRight,
  Search,
  Filter,
  ChevronLeft,
  ChevronRight,
  HelpCircle,
  BarChart,
  PieChart,
  Bell,
  Settings,
  Shield,
  Users
} from 'lucide-react';

const FacturationModal = () => {
  // États
  const [activeTab, setActiveTab] = useState('dashboard');
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('mtn');
  const [filterYear, setFilterYear] = useState('2025');
  const [filterMonth, setFilterMonth] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [yearDropdownOpen, setYearDropdownOpen] = useState(false);
  const [monthDropdownOpen, setMonthDropdownOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [showFeatureInfo, setShowFeatureInfo] = useState(false);

  // Données de démonstration (peuvent être remplacées par des appels API)
  const subscription = {
    startDate: '15/01/2025',
    endDate: '15/01/2026',
    status: 'active', // active, expired, pending
    plan: 'Standard',
    licenseCode: 'SCH-STD-2025-01-15-ABCXYZ',
    autoRenew: true,
    featuresUsed: {
      storage: 65, // pourcentage utilisé
      users: 42, // pourcentage utilisé
      modules: 75 // pourcentage utilisé
    },
    paymentHistory: {
      onTime: 12,
      late: 2,
      missed: 0
    }
  };

  const invoices = [
    { id: 'F2025-001', date: '15/01/2025', amount: '600 USD', period: 'Jan 2025 - Jan 2026', status: 'paid', paymentMethod: 'Carte bancaire', reference: 'REF-87654321' },
    { id: 'F2025-002', date: '15/02/2025', amount: '50 USD', period: 'Février 2025', status: 'paid', paymentMethod: 'MTN Money', reference: 'MTN-12345678' },
    { id: 'F2025-003', date: '15/03/2025', amount: '50 USD', period: 'Mars 2025', status: 'paid', paymentMethod: 'Airtel Money', reference: 'AIR-98765432' },
    { id: 'F2025-004', date: '15/04/2025', amount: '50 USD', period: 'Avril 2025', status: 'pending', dueDate: '25/04/2025' },
    { id: 'F2025-005', date: '15/05/2025', amount: '50 USD', period: 'Mai 2025', status: 'draft', dueDate: '25/05/2025' }
  ];

  const plans = [
    { 
      id: 'standard', 
      name: 'Standard', 
      price: '600 USD/an', 
      monthlyPrice: '50 USD/mois',
      features: [
        '500 élèves maximum',
        'Stockage 5 GB',
        '3 utilisateurs administratifs',
        'Modules de base (Admissions, Notes, Présence)',
        'Support par email sous 48h',
        'Mises à jour de sécurité'
      ],
      recommended: false
    },
    { 
      id: 'pro', 
      name: 'Pro', 
      price: '1200 USD/an', 
      monthlyPrice: '100 USD/mois',
      features: [
        '2000 élèves maximum',
        'Stockage 20 GB',
        '10 utilisateurs administratifs',
        'Tous les modules',
        'Support prioritaire sous 24h',
        'Mises à jour régulières',
        'Personnalisation limitée',
        'API pour intégrations externes'
      ],
      recommended: true
    },
    { 
      id: 'premium', 
      name: 'Premium', 
      price: '2400 USD/an', 
      monthlyPrice: '200 USD/mois',
      features: [
        'Élèves illimités',
        'Stockage 100 GB',
        'Utilisateurs administratifs illimités',
        'Tous les modules avec options avancées',
        'Support dédié 24/7',
        'Accès anticipé aux nouvelles fonctionnalités',
        'Personnalisation complète',
        'API complète et webhooks',
        'Formation personnalisée incluse'
      ],
      recommended: false
    }
  ];

  // Données pour le graphique (simulées)
  const monthNames = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];
  const currentDate = new Date();
  const currentMonth = currentDate.getMonth();
  
  const paymentChartData = [];
for (let i = 5; i >= 0; i--) {
  const monthIndex = (currentMonth - i + 12) % 12; // Assure un index positif avec le modulo
  paymentChartData.push({
    month: monthNames[monthIndex] || "Inconnu", // Valeur de secours si monthName est undefined
    amount: i === 0 ? 0 : 50 // Mois courant (pas encore payé)
  });
}

  // Filtrage des factures
  const filteredInvoices = invoices.filter(invoice => {
    if (filterStatus !== 'all' && filterStatus !== invoice.status) return false;
    return true;
  });

  // Fonction pour afficher le statut
  const renderStatus = (status) => {
    switch(status) {
      case 'active':
        return (
          <span className="px-2 py-1 bg-[#319190] bg-opacity-20 text-[#319190] rounded-full font-medium text-xs flex items-center">
            <CheckCircle size={14} className="mr-1" /> Actif
          </span>
        );
      case 'expired':
        return (
          <span className="px-2 py-1 bg-[#c66d59] bg-opacity-20 text-[#c66d59] rounded-full font-medium text-xs flex items-center">
            <AlertCircle size={14} className="mr-1" /> Expiré
          </span>
        );
      case 'pending':
        return (
          <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full font-medium text-xs flex items-center">
            <Clock size={14} className="mr-1" /> En attente
          </span>
        );
      case 'paid':
        return (
          <span className="px-2 py-1 bg-[#319190] bg-opacity-20 text-[#319190] rounded-full font-medium text-xs flex items-center">
            <CheckCircle size={14} className="mr-1" /> Payé
          </span>
        );
      case 'draft':
        return (
          <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded-full font-medium text-xs flex items-center">
            <FileText size={14} className="mr-1" /> Brouillon
          </span>
        );
      default:
        return null;
    }
  };

  // Pour la pagination
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  return (
    <div className="bg-gray-50 min-h-screen">
      <div className="w-full">
        {/* Entête fixe */}
        <div className="bg-white border-b shadow-sm sticky top-0 z-10">
        <div className="w-full px-6 py-4 flex justify-between items-center">
          
            <div className="flex items-center gap-4">
              <span className="text-sm text-gray-500">
                Prochaine facture: <span className="font-medium text-gray-800">15 avril 2025</span>
              </span>
              <button className="text-gray-500 hover:text-gray-700">
                <X size={24} />
              </button>
            </div>
          </div>
          
          {/* Navigation par onglets */}
          <div className="w-full px-6">
            <div className="flex border-b border-gray-200">
              <button
                className={`px-4 py-3 font-medium text-sm ${
                  activeTab === 'dashboard'
                    ? 'text-[#246990] border-b-2 border-[#246990]'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
                onClick={() => setActiveTab('dashboard')}
              >
                Tableau de bord
              </button>
              <button
                className={`px-4 py-3 font-medium text-sm ${
                  activeTab === 'factures'
                    ? 'text-[#246990] border-b-2 border-[#246990]'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
                onClick={() => setActiveTab('factures')}
              >
                Factures
              </button>
              <button
                className={`px-4 py-3 font-medium text-sm ${
                  activeTab === 'paiement'
                    ? 'text-[#246990] border-b-2 border-[#246990]'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
                onClick={() => setActiveTab('paiement')}
              >
                Paiement
              </button>
              <button
                className={`px-4 py-3 font-medium text-sm ${
                  activeTab === 'abonnement'
                    ? 'text-[#246990] border-b-2 border-[#246990]'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
                onClick={() => setActiveTab('abonnement')}
              >
                Gérer l'abonnement
              </button>
            </div>
          </div>
        </div>

        <div className="w-full px-6 py-8">
          {/* Contenu des onglets */}
          {activeTab === 'dashboard' && (
           
<div>
    
<div className="space-y-6">
  {/* Résumé de l'abonnement */}
  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
    <div className="lg:col-span-2">
      <div className="bg-gradient-to-r from-[#246990] to-[#3084b5] text-white rounded-xl p-6 h-full">
        <div className="flex justify-between items-start">
          <div>
            <h2 className="text-xl font-semibold mb-4">Résumé de votre abonnement</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <p className="text-sm text-white/80 flex items-center">
                  <Calendar size={16} className="mr-2 text-white/60" />
                  Période d'abonnement
                </p>
                <p className="text-base font-medium mt-1">
                  {subscription.startDate} - {subscription.endDate}
                </p>
              </div>
              
              <div>
                <p className="text-sm text-white/80 flex items-center">
                  <Star size={16} className="mr-2 text-white/60" />
                  Formule
                </p>
                <p className="text-base font-medium mt-1">
                  {subscription.plan}
                </p>
              </div>
              
              <div>
                <p className="text-sm text-white/80 flex items-center">
                  <Clock size={16} className="mr-2 text-white/60" />
                  Statut
                </p>
                <div className="mt-1">
                  <span className="px-2 py-1 bg-white/20 text-white rounded-full font-medium text-xs">
                    {subscription.status === 'active' ? 'Actif' : 
                     subscription.status === 'expired' ? 'Expiré' : 'En attente'}
                  </span>
                </div>
              </div>
              
              <div>
                <p className="text-sm text-white/80 flex items-center">
                  <RefreshCw size={16} className="mr-2 text-white/60" />
                  Renouvellement automatique
                </p>
                <p className="text-base font-medium mt-1 flex items-center">
                  {subscription.autoRenew ? (
                    <span className="flex items-center">
                      <Check size={16} className="mr-1" /> Activé
                    </span>
                  ) : (
                    <span className="flex items-center">
                      <X size={16} className="mr-1" /> Désactivé
                    </span>
                  )}
                </p>
              </div>
            </div>
            
            <div>
              <p className="text-sm text-white/80 flex items-center mb-2">
                <Shield size={16} className="mr-2 text-white/60" />
                Code de licence
              </p>
              <div className="flex items-center space-x-2">
                <code className="bg-white/10 p-2 rounded text-sm font-mono select-all">
                  {subscription.licenseCode}
                </code>
                <button className="text-white hover:text-white/80 text-sm underline">
                  Copier
                </button>
              </div>
            </div>
          </div>
          
          <div className="flex flex-col space-y-2">
            <button className="px-4 py-2 bg-white text-[#246990] rounded-md hover:bg-white/90 inline-flex items-center">
              <RefreshCw size={16} className="mr-2" />
              Renouveler
            </button>
            <button className="px-4 py-2 bg-white/20 text-white rounded-md hover:bg-white/30 inline-flex items-center">
              Changer d'offre
            </button>
          </div>
        </div>
      </div>
    </div>
    
    <div className="lg:col-span-1">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 h-full">
        <h3 className="text-lg font-semibold mb-4 text-gray-800">Prochaine facturation</h3>
        
        <div className="bg-gray-50 p-4 rounded-lg mb-4">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm text-gray-500">Date</span>
            <span className="font-medium">15 avril 2025</span>
          </div>
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm text-gray-500">Montant</span>
            <span className="font-medium">50 USD</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-500">Statut</span>
            <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs font-medium">À venir</span>
          </div>
        </div>
        
        <button 
          className="w-full mt-2 px-4 py-2 bg-[#246990] text-white rounded-md hover:bg-[#3084b5]"
          onClick={() => {
            setActiveTab('paiement');
            setShowPaymentForm(true);
          }}
        >
          Payer maintenant
        </button>
      </div>
    </div>
  </div>
  
  {/* Usage et statistiques */}
  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
    <div className="lg:col-span-1">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold mb-4 text-gray-800">Utilisation des ressources</h3>
        
        <div className="space-y-4">
          <div>
            <div className="flex justify-between items-center mb-1">
              <span className="text-sm text-gray-500 flex items-center">
                <FileText size={16} className="mr-1" /> 
                Stockage
              </span>
              <span className="text-sm font-medium">
                {subscription.featuresUsed.storage}%
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className={`h-2 rounded-full ${
                  subscription.featuresUsed.storage > 80 
                    ? 'bg-[#c66d59]' 
                    : subscription.featuresUsed.storage > 60 
                      ? 'bg-yellow-500' 
                      : 'bg-[#319190]'
                }`}
                style={{ width: `${subscription.featuresUsed.storage}%` }}
              ></div>
            </div>
          </div>
          
          <div>
            <div className="flex justify-between items-center mb-1">
              <span className="text-sm text-gray-500 flex items-center">
                <Users size={16} className="mr-1" /> 
                Utilisateurs
              </span>
              <span className="text-sm font-medium">
                {subscription.featuresUsed.users}%
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="h-2 rounded-full bg-[#319190]"
                style={{ width: `${subscription.featuresUsed.users}%` }}
              ></div>
            </div>
          </div>
          
          <div>
            <div className="flex justify-between items-center mb-1">
              <span className="text-sm text-gray-500 flex items-center">
                <Settings size={16} className="mr-1" /> 
                Modules
              </span>
              <span className="text-sm font-medium">
                {subscription.featuresUsed.modules}%
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="h-2 rounded-full bg-[#3084b5]"
                style={{ width: `${subscription.featuresUsed.modules}%` }}
              ></div>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <div className="lg:col-span-2">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold mb-4 text-gray-800">Historique des paiements</h3>
        
        <div className="h-64 flex items-center justify-center">
          <div className="w-full h-full flex flex-col">
            <div className="flex-grow">
              {/* Graphique simplifié */}
              <div className="bg-gray-50 h-full w-full rounded-lg flex items-end justify-between p-4">
                {paymentChartData.map((data, index) => (
                  <div key={index} className="flex flex-col items-center">
                    <div 
                      className="bg-[#246990] w-12 rounded-t-sm" 
                      style={{ 
                        height: `${data.amount * 2}px`,
                        opacity: index === paymentChartData.length - 1 ? '0.5' : '1'
                      }}
                    ></div>
                    <span className="text-xs mt-2 text-gray-500">{data.month.substring(0, 3)}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex justify-between mt-4 text-sm text-gray-600">
              <div>
                <span className="inline-block w-3 h-3 bg-[#246990] mr-1"></span>
                <span>Payé</span>
              </div>
              <div>
                <span className="inline-block w-3 h-3 bg-[#246990] opacity-50 mr-1"></span>
                <span>En attente</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>
</div>

          )}
          
          {activeTab === 'factures' && (        
<div>
<div className="space-y-6">
  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
    <div className="flex justify-between items-center mb-6">
      <h2 className="text-lg font-semibold text-gray-800">Historique des factures</h2>
      
      <div className="flex items-center space-x-3">
        {/* Recherche */}
        <div className="relative">
          <input
            type="text"
            placeholder="Rechercher une facture..."
            className="pl-9 pr-4 py-2 border border-gray-300 rounded-md text-sm w-64"
          />
          <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
        </div>
        
        {/* Filtre par année */}
        <div className="relative">
          <button 
            className="px-3 py-2 border border-gray-300 rounded-md text-sm flex items-center"
            onClick={() => setYearDropdownOpen(!yearDropdownOpen)}
          >
            <span className="mr-1">Année: </span>
            {filterYear}
            <ChevronDown size={16} className="ml-1" />
          </button>
          
          {yearDropdownOpen && (
            <div className="absolute right-0 mt-1 w-32 bg-white rounded-md shadow-lg z-10 border border-gray-200">
              <ul className="py-1">
                <li 
                  className="px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer"
                  onClick={() => {
                    setFilterYear('2025');
                    setYearDropdownOpen(false);
                  }}
                >
                  2025
                </li>
                <li 
                  className="px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer"
                  onClick={() => {
                    setFilterYear('2024');
                    setYearDropdownOpen(false);
                  }}
                >
                  2024
                </li>
              </ul>
            </div>
          )}
        </div>
        
        {/* Filtre par mois */}
        <div className="relative">
          <button 
            className="px-3 py-2 border border-gray-300 rounded-md text-sm flex items-center"
            onClick={() => setMonthDropdownOpen(!monthDropdownOpen)}
          >
            <span className="mr-1">Mois: </span>
            {filterMonth === 'all' ? 'Tous' : filterMonth}
            <ChevronDown size={16} className="ml-1" />
          </button>
          
          {monthDropdownOpen && (
            <div className="absolute right-0 mt-1 w-40 bg-white rounded-md shadow-lg z-10 border border-gray-200">
              <ul className="py-1">
                <li 
                  className="px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer"
                  onClick={() => {
                    setFilterMonth('all');
                    setMonthDropdownOpen(false);
                  }}
                >
                  Tous les mois
                </li>
                {monthNames.map((month, index) => (
                  <li 
                    key={index}
                    className="px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer"
                    onClick={() => {
                      setFilterMonth(month);
                      setMonthDropdownOpen(false);
                    }}
                  >
                    {month}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
        
        {/* Filtre par statut */}
        <select
          className="px-3 py-2 border border-gray-300 rounded-md text-sm"
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
        >
          <option value="all">Tous les statuts</option>
          <option value="paid">Payé</option>
          <option value="pending">En attente</option>
          <option value="draft">Brouillon</option>
        </select>
      </div>
    </div>
    
    {/* Tableau des factures */}
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead>
          <tr className="bg-gray-50">
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Facture #</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Montant</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Période</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Statut</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Méthode de paiement
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {filteredInvoices.map((invoice) => (
            <tr key={invoice.id} className="hover:bg-gray-50">
              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-[#246990]">
                {invoice.id}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                {invoice.date}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                {invoice.amount}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                {invoice.period}
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                {renderStatus(invoice.status)}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                {invoice.paymentMethod || '-'}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm">
                {invoice.status === 'paid' ? (
                  <button className="text-[#246990] hover:text-[#3084b5] flex items-center">
                    <Download size={16} className="mr-1" />
                    Télécharger
                  </button>
                ) : invoice.status === 'pending' ? (
                  <button 
                    className="text-[#246990] hover:text-[#3084b5] flex items-center"
                    onClick={() => {
                      setActiveTab('paiement');
                      setShowPaymentForm(true);
                    }}
                  >
                    <CreditCard size={16} className="mr-1" />
                    Payer maintenant
                  </button>
                ) : (
                  <span className="text-gray-400">-</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    
    {/* Pagination */}
    <div className="mt-6 flex justify-between items-center">
      <div className="text-sm text-gray-500">
        Affichage de <span className="font-medium">{filteredInvoices.length}</span> factures
      </div>
      
      <div className="flex space-x-1">
        <button 
          className="px-3 py-2 border border-gray-300 rounded-md text-sm flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={currentPage === 1}
          onClick={() => handlePageChange(currentPage - 1)}
        >
          <ChevronLeft size={16} className="mr-1" />
          Précédent
        </button>
        
        <button 
          className="px-3 py-2 border border-gray-300 rounded-md text-sm flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={currentPage * 10 >= filteredInvoices.length}
          onClick={() => handlePageChange(currentPage + 1)}
        >
          Suivant
          <ChevronRight size={16} className="ml-1" />
        </button>
      </div>
    </div>
  </div>
</div>
</div>
)}


          {activeTab === 'paiement' && (
           
            <div>
<div className="space-y-6">
  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
    <h2 className="text-lg font-semibold text-gray-800 mb-6">Paiement</h2>
    
    {showPaymentForm ? (
      <div>
        <div className="bg-gradient-to-r from-[#319190] to-[#63b5b4] text-white rounded-xl p-4 mb-6 flex items-start">
          <Info className="h-5 w-5 mr-3 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="text-sm font-medium">Facture en attente de paiement</h3>
            <div className="mt-2 text-sm opacity-90">
              <p>Facture #F2025-004 - 50 USD pour la période Avril 2025</p>
              <p className="mt-1">Date limite de paiement: 25/04/2025</p>
            </div>
          </div>
        </div>
        
        <div className="mb-8">
          <h3 className="text-md font-medium mb-4">Choisissez votre méthode de paiement</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <button
              className={`border rounded-lg p-4 flex items-center ${
                selectedPaymentMethod === 'mtn' 
                  ? 'border-[#246990] bg-[#246990]/5' 
                  : 'border-gray-200 hover:bg-gray-50'
              }`}
              onClick={() => setSelectedPaymentMethod('mtn')}
            >
              <div className="w-10 h-10 bg-yellow-500 rounded-lg flex items-center justify-center mr-3">
                <span className="text-white font-bold">MTN</span>
              </div>
              <div>
                <span className="font-medium block">MTN Money</span>
                <span className="text-xs text-gray-500">Paiement mobile</span>
              </div>
            </button>
            
            <button
              className={`border rounded-lg p-4 flex items-center ${
                selectedPaymentMethod === 'airtel' 
                  ? 'border-[#246990] bg-[#246990]/5' 
                  : 'border-gray-200 hover:bg-gray-50'
              }`}
              onClick={() => setSelectedPaymentMethod('airtel')}
            >
              <div className="w-10 h-10 bg-red-600 rounded-lg flex items-center justify-center mr-3">
                <span className="text-white font-bold">A</span>
              </div>
              <div>
                <span className="font-medium block">Airtel Money</span>
                <span className="text-xs text-gray-500">Paiement mobile</span>
              </div>
            </button>
            
            <button
              className={`border rounded-lg p-4 flex items-center ${
                selectedPaymentMethod === 'card' 
                  ? 'border-[#246990] bg-[#246990]/5' 
                  : 'border-gray-200 hover:bg-gray-50'
              }`}
              onClick={() => setSelectedPaymentMethod('card')}
            >
              <div className="w-10 h-10 bg-[#246990] rounded-lg flex items-center justify-center mr-3">
                <CreditCard size={18} className="text-white" />
              </div>
              <div>
                <span className="font-medium block">Carte bancaire</span>
                <span className="text-xs text-gray-500">Visa, Mastercard</span>
              </div>
            </button>
          </div>
        </div>
        
        {selectedPaymentMethod === 'mtn' && (
          <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
            <div className="flex items-start mb-6">
              <div className="w-16 h-16 bg-yellow-500 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                <span className="text-white font-bold text-xl">MTN</span>
              </div>
              <div>
                <h3 className="text-lg font-medium mb-1">Paiement par MTN Money</h3>
                <p className="text-sm text-gray-600">
                  Effectuez votre paiement en utilisant votre compte MTN Money.
                </p>
              </div>
            </div>
            
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
              <h4 className="font-medium text-yellow-800 mb-2">Instructions de paiement</h4>
              <ol className="list-decimal pl-5 text-sm text-yellow-800 space-y-2">
                <li>Composez le <span className="font-mono font-bold">*165#</span> sur votre téléphone</li>
                <li>Sélectionnez "Payer des factures"</li>
                <li>Entrez le numéro marchand: <span className="font-mono font-bold">12345</span></li>
                <li>Entrez le montant: <span className="font-mono font-bold">50 USD</span></li>
                <li>Entrez la référence: <span className="font-mono font-bold">F2025-004</span></li>
                <li>Confirmez avec votre code PIN</li>
              </ol>
            </div>
            
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm text-gray-500">Montant à payer</span>
                <span className="font-medium text-lg">50 USD</span>
              </div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm text-gray-500">Frais de transaction</span>
                <span className="text-sm">0 USD</span>
              </div>
              <div className="border-t border-gray-200 my-2"></div>
              <div className="flex justify-between items-center">
                <span className="font-medium">Total</span>
                <span className="font-medium text-lg">50 USD</span>
              </div>
            </div>
            
            <div className="flex justify-between">
              <button 
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
                onClick={() => setSelectedPaymentMethod('')}
              >
                Retour
              </button>
              <button className="px-6 py-2 bg-[#246990] text-white rounded-md hover:bg-[#3084b5]">
                J'ai effectué le paiement
              </button>
            </div>
          </div>
        )}
        
        {selectedPaymentMethod === 'airtel' && (
          <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
            <div className="flex items-start mb-6">
              <div className="w-16 h-16 bg-red-600 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                <span className="text-white font-bold text-xl">A</span>
              </div>
              <div>
                <h3 className="text-lg font-medium mb-1">Paiement par Airtel Money</h3>
                <p className="text-sm text-gray-600">
                  Effectuez votre paiement en utilisant votre compte Airtel Money.
                </p>
              </div>
            </div>
            
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <h4 className="font-medium text-red-800 mb-2">Instructions de paiement</h4>
              <ol className="list-decimal pl-5 text-sm text-red-800 space-y-2">
                <li>Composez le <span className="font-mono font-bold">*150*4#</span> sur votre téléphone</li>
                <li>Sélectionnez "Paiement"</li>
                <li>Entrez le numéro marchand: <span className="font-mono font-bold">54321</span></li>
                <li>Entrez le montant: <span className="font-mono font-bold">50 USD</span></li>
                <li>Entrez la référence: <span className="font-mono font-bold">F2025-004</span></li>
                <li>Confirmez avec votre code PIN</li>
              </ol>
            </div>
            
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm text-gray-500">Montant à payer</span>
                <span className="font-medium text-lg">50 USD</span>
              </div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm text-gray-500">Frais de transaction</span>
                <span className="text-sm">0 USD</span>
              </div>
              <div className="border-t border-gray-200 my-2"></div>
              <div className="flex justify-between items-center">
                <span className="font-medium">Total</span>
                <span className="font-medium text-lg">50 USD</span>
              </div>
            </div>
            
            <div className="flex justify-between">
              <button 
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
                onClick={() => setSelectedPaymentMethod('')}
              >
                Retour
              </button>
              <button className="px-6 py-2 bg-[#246990] text-white rounded-md hover:bg-[#3084b5]">
                J'ai effectué le paiement
              </button>
            </div>
          </div>
        )}
        
        {selectedPaymentMethod === 'card' && (
          <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
            <div className="flex items-start mb-6">
              <div className="w-16 h-16 bg-[#246990] rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                <CreditCard size={32} className="text-white" />
              </div>
              <div>
                <h3 className="text-lg font-medium mb-1">Paiement par carte bancaire</h3>
                <p className="text-sm text-gray-600">
                  Effectuez un paiement sécurisé avec votre carte Visa ou Mastercard.
                </p>
              </div>
            </div>
            
            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Numéro de carte
                </label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  placeholder="1234 5678 9012 3456"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Date d'expiration
                  </label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    placeholder="MM/AA"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    CVV
                  </label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    placeholder="123"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nom sur la carte
                </label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  placeholder="John Doe"
                />
              </div>
            </div>
            
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm text-gray-500">Montant à payer</span>
                <span className="font-medium text-lg">50 USD</span>
              </div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm text-gray-500">Frais de transaction</span>
                <span className="text-sm">0 USD</span>
              </div>
              <div className="border-t border-gray-200 my-2"></div>
              <div className="flex justify-between items-center">
                <span className="font-medium">Total</span>
                <span className="font-medium text-lg">50 USD</span>
              </div>
            </div>
            
            <div className="flex justify-between">
              <button 
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
                onClick={() => setSelectedPaymentMethod('')}
              >
                Retour
              </button>
              <button className="px-6 py-2 bg-[#246990] text-white rounded-md hover:bg-[#3084b5]">
                Payer 50 USD
              </button>
            </div>
          </div>
        )}
      </div>
    ) : (
      <div className="text-center py-12">
        <div className="mx-auto bg-gray-100 rounded-full w-20 h-20 flex items-center justify-center mb-4">
          <CreditCard size={32} className="text-gray-400" />
        </div>
        <h3 className="text-lg font-medium text-gray-700 mb-2">Aucune facture en attente de paiement</h3>
        <p className="text-gray-500 mb-6 max-w-lg mx-auto">
          Toutes vos factures sont à jour. Si vous souhaitez effectuer un paiement anticipé ou modifier votre abonnement, 
          vous pouvez le faire depuis la section gestion du contrat.
        </p>
        <div className="flex justify-center space-x-4">
          <button 
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 inline-flex items-center"
            onClick={() => setActiveTab('factures')}
          >
            <FileText size={16} className="mr-2" />
            Voir mes factures
          </button>
          <button 
            className="px-4 py-2 bg-[#246990] text-white rounded-md hover:bg-[#3084b5] inline-flex items-center"
            onClick={() => setActiveTab('abonnement')}
          >
            <Star size={16} className="mr-2" />
            Gérer mon abonnement
          </button>
        </div>
      </div>
    )}
  </div>
</div>
</div>
          )}
          
          {activeTab === 'abonnement' && (<div>
           

<div className="space-y-6">
  {/* Options de renouvellement */}
  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
    <h2 className="text-lg font-semibold text-gray-800 mb-6">Paramètres de l'abonnement</h2>
    
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div className="space-y-6">
        <div>
          <h3 className="text-md font-medium mb-3">Options de renouvellement</h3>
          <div className="flex items-center mb-4">
            <div className="relative inline-block w-10 mr-2 align-middle select-none">
              <input 
                type="checkbox" 
                name="autoRenew" 
                id="autoRenew" 
                checked={subscription.autoRenew}
                className="toggle-checkbox absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer"
                style={{ 
                  right: subscription.autoRenew ? '0' : 'auto',
                  borderColor: subscription.autoRenew ? '#319190' : 'gray',
                  backgroundColor: subscription.autoRenew ? '#319190' : 'white'
                }}
                onChange={() => console.log('Toggle auto-renew')}
              />
              <label 
                htmlFor="autoRenew" 
                className="toggle-label block overflow-hidden h-6 rounded-full bg-gray-300 cursor-pointer"
                style={{ backgroundColor: subscription.autoRenew ? '#319190' : '#ccc', opacity: subscription.autoRenew ? 1 : 0.6 }}
              ></label>
            </div>
            <label htmlFor="autoRenew" className="text-sm text-gray-700">
              Activer le renouvellement automatique
            </label>
          </div>
          <p className="text-sm text-gray-500 mb-4">
            Avec le renouvellement automatique, votre abonnement sera renouvelé automatiquement à la fin de la période en cours,
            aux tarifs en vigueur à cette date.
          </p>
        </div>
        
        <div>
          <h3 className="text-md font-medium mb-3">Notification d'expiration</h3>
          <div className="space-y-2">
            <div className="flex items-center">
              <input
                type="checkbox"
                id="notify30"
                className="h-4 w-4 text-[#246990] border-gray-300 rounded mr-2"
                defaultChecked
              />
              <label htmlFor="notify30" className="text-sm text-gray-700">
                Me notifier 30 jours avant l'expiration
              </label>
            </div>
            <div className="flex items-center">
              <input
                type="checkbox"
                id="notify15"
                className="h-4 w-4 text-[#246990] border-gray-300 rounded mr-2"
                defaultChecked
              />
              <label htmlFor="notify15" className="text-sm text-gray-700">
                Me notifier 15 jours avant l'expiration
              </label>
            </div>
            <div className="flex items-center">
              <input
                type="checkbox"
                id="notify7"
                className="h-4 w-4 text-[#246990] border-gray-300 rounded mr-2"
                defaultChecked
              />
              <label htmlFor="notify7" className="text-sm text-gray-700">
                Me notifier 7 jours avant l'expiration
              </label>
            </div>
            <div className="flex items-center">
              <input
                type="checkbox"
                id="notify1"
                className="h-4 w-4 text-[#246990] border-gray-300 rounded mr-2"
                defaultChecked
              />
              <label htmlFor="notify1" className="text-sm text-gray-700">
                Me notifier 1 jour avant l'expiration
              </label>
            </div>
          </div>
        </div>
        
        <div>
          <h3 className="text-md font-medium mb-3">Mode de facturation</h3>
          <div className="space-y-2">
            <div className="flex items-center">
              <input
                type="radio"
                id="billingAnnual"
                name="billingCycle"
                className="h-4 w-4 text-[#246990] border-gray-300 mr-2"
                defaultChecked
              />
              <label htmlFor="billingAnnual" className="text-sm text-gray-700">
                Annuel (économisez 15%)
              </label>
            </div>
            <div className="flex items-center">
              <input
                type="radio"
                id="billingMonthly"
                name="billingCycle"
                className="h-4 w-4 text-[#246990] border-gray-300 mr-2"
              />
              <label htmlFor="billingMonthly" className="text-sm text-gray-700">
                Mensuel
              </label>
            </div>
          </div>
        </div>
        
        <div className="pt-4 flex space-x-3">
          <button className="px-4 py-2 bg-[#246990] text-white rounded-md hover:bg-[#3084b5]">
            Enregistrer les préférences
          </button>
          <button className="px-4 py-2 text-[#c66d59] hover:bg-[#c66d59]/5 rounded-md flex items-center">
            Annuler mon abonnement
          </button>
        </div>
      </div>
      
      <div className="bg-gradient-to-r from-[#319190] to-[#63b5b4] text-white rounded-xl p-6">
        <h3 className="text-lg font-semibold mb-3">Détails de votre abonnement actuel</h3>
        <div className="space-y-4">
          <div>
            <p className="text-sm opacity-80">Formule</p>
            <p className="font-medium text-lg">{subscription.plan}</p>
          </div>
          <div>
            <p className="text-sm opacity-80">Période d'abonnement</p>
            <p className="font-medium">{subscription.startDate} - {subscription.endDate}</p>
          </div>
          <div>
            <p className="text-sm opacity-80">Montant</p>
            <p className="font-medium text-lg">600 USD / an</p>
          </div>
          <div>
            <p className="text-sm opacity-80">État de l'abonnement</p>
            <p className="font-medium flex items-center">
              <CheckCircle size={16} className="mr-1" /> Actif
            </p>
          </div>
          <div>
            <p className="text-sm opacity-80">Prochain renouvellement</p>
            <p className="font-medium">15 janvier 2026</p>
          </div>
        </div>
      </div>
    </div>
  </div>
  
  {/* Comparaison des forfaits */}
  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
    <h2 className="text-lg font-semibold text-gray-800 mb-6">Changer de formule d'abonnement</h2>
    <p className="text-sm text-gray-500 mb-6">
      Vous pouvez changer de formule à tout moment. Si vous passez à une formule supérieure, vous ne paierez que la différence.
      Si vous passez à une formule inférieure, le changement prendra effet à la fin de votre période d'abonnement actuelle.
    </p>
    
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-4">
      {plans.map((plan) => (
        <div 
          key={plan.id} 
          className={`border rounded-xl p-6 transition-all hover:shadow-md relative ${
            plan.name === subscription.plan 
              ? 'border-[#319190] bg-[#319190]/5' 
              : 'border-gray-200'
          } ${plan.recommended ? 'border-[#3084b5]' : ''}`}
        >
          {plan.recommended && (
            <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-[#3084b5] text-white px-3 py-1 rounded-full text-xs font-medium">
              Recommandé
            </div>
          )}
          <h4 className="font-semibold text-gray-900 text-xl mb-2">{plan.name}</h4>
          <div className="mb-4">
            <span className="text-[#246990] font-bold text-2xl">{plan.price}</span>
            <span className="text-sm text-gray-500 block mt-1">ou {plan.monthlyPrice}</span>
          </div>
          <ul className="text-sm text-gray-600 space-y-3 mb-6">
            {plan.features.map((feature, index) => (
              <li key={index} className="flex items-start">
                <CheckCircle size={16} className="text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                {feature}
              </li>
            ))}
          </ul>
          {plan.name === subscription.plan ? (
            <p className="text-sm font-medium text-[#319190]">Formule actuelle</p>
          ) : (
            <button 
              className="w-full mt-2 px-4 py-2 bg-white border border-[#246990] text-[#246990] rounded-md hover:bg-[#246990]/5"
            >
              Sélectionner cette formule
            </button>
          )}
        </div>
      ))}
    </div>
  </div>

  {/* FAQ */}
  <div className="bg-gradient-to-r from-[#c66d59] to-[#ae7467] text-white rounded-xl p-6">
    <div className="flex items-start mb-4">
      <HelpCircle size={24} className="mr-3 flex-shrink-0" />
      <div>
        <h2 className="text-lg font-semibold mb-2">Questions fréquentes</h2>
        <p className="text-white/80">Consultez les réponses aux questions les plus courantes concernant votre abonnement.</p>
      </div>
    </div>
    
    <div className="space-y-4 mt-6">
      <div className="bg-white/10 rounded-lg p-4">
        <h3 className="font-medium mb-2">Comment fonctionne le renouvellement automatique ?</h3>
        <p className="text-sm text-white/90">
          Lorsque le renouvellement automatique est activé, votre abonnement sera renouvelé 7 jours avant sa date d'expiration. 
          Vous recevrez une notification par e-mail vous informant du renouvellement à venir.
        </p>
      </div>
      
      <div className="bg-white/10 rounded-lg p-4">
        <h3 className="font-medium mb-2">Puis-je changer de formule en cours d'abonnement ?</h3>
        <p className="text-sm text-white/90">
          Oui, vous pouvez passer à une formule supérieure à tout moment. La différence de prix sera calculée 
          au prorata de la période restante. Si vous souhaitez passer à une formule inférieure, le changement 
          prendra effet à la fin de votre période d'abonnement actuelle.
        </p>
      </div>
      
      <div className="bg-white/10 rounded-lg p-4">
        <h3 className="font-medium mb-2">Comment annuler mon abonnement ?</h3>
        <p className="text-sm text-white/90">
          Vous pouvez annuler votre abonnement à tout moment en cliquant sur le bouton "Annuler mon abonnement" 
          dans les paramètres. L'annulation prendra effet à la fin de la période de facturation en cours. 
          Notez que vous ne serez pas remboursé pour la période non utilisée.
        </p>
      </div>
      
      <div className="bg-white/10 rounded-lg p-4">
        <h3 className="font-medium mb-2">Quels sont les moyens de paiement acceptés ?</h3>
        <p className="text-sm text-white/90">
          Nous acceptons les paiements par carte bancaire (Visa, Mastercard), MTN Money et Airtel Money. 
          D'autres options de paiement peuvent être disponibles selon votre région.
        </p>
      </div>
    </div>
    
    <div className="mt-6 flex justify-center">
      <button className="flex items-center px-4 py-2 bg-white text-[#c66d59] rounded-md hover:bg-white/90">
        <HelpCircle size={16} className="mr-2" />
        Contacter le support
      </button>
    </div>
  </div>
  
  {/* Historique des modifications */}
  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
    <h2 className="text-lg font-semibold text-gray-800 mb-6">Historique des modifications</h2>
    
    <div className="relative">
      <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200"></div>
      
      <div className="space-y-6">
        <div className="relative pl-10">
          <div className="absolute left-3 -translate-x-1/2 w-4 h-4 rounded-full bg-[#246990] border-2 border-white"></div>
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex justify-between mb-1">
              <h3 className="font-medium">Renouvellement de l'abonnement</h3>
              <span className="text-sm text-gray-500">15/01/2025</span>
            </div>
            <p className="text-sm text-gray-600">
              Renouvellement de l'abonnement Standard pour une durée de 12 mois.
            </p>
            <p className="text-sm text-gray-500 mt-2">
              Montant: 600 USD • Référence: REF-87654321
            </p>
          </div>
        </div>
        
        <div className="relative pl-10">
          <div className="absolute left-3 -translate-x-1/2 w-4 h-4 rounded-full bg-gray-300 border-2 border-white"></div>
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex justify-between mb-1">
              <h3 className="font-medium">Changement des préférences de notification</h3>
              <span className="text-sm text-gray-500">10/01/2025</span>
            </div>
            <p className="text-sm text-gray-600">
              Activation des notifications à 30, 15, 7 et 1 jour avant l'expiration de l'abonnement.
            </p>
          </div>
        </div>
        
        <div className="relative pl-10">
          <div className="absolute left-3 -translate-x-1/2 w-4 h-4 rounded-full bg-gray-300 border-2 border-white"></div>
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex justify-between mb-1">
              <h3 className="font-medium">Activation du renouvellement automatique</h3>
              <span className="text-sm text-gray-500">05/01/2025</span>
            </div>
            <p className="text-sm text-gray-600">
              Activation du renouvellement automatique pour l'abonnement.
            </p>
          </div>
        </div>
        
        <div className="relative pl-10">
          <div className="absolute left-3 -translate-x-1/2 w-4 h-4 rounded-full bg-gray-300 border-2 border-white"></div>
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex justify-between mb-1">
              <h3 className="font-medium">Création de l'abonnement</h3>
              <span className="text-sm text-gray-500">15/01/2024</span>
            </div>
            <p className="text-sm text-gray-600">
              Création de l'abonnement Standard pour une durée de 12 mois.
            </p>
            <p className="text-sm text-gray-500 mt-2">
              Montant: 600 USD • Référence: REF-12345678
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
  <div>
  </div>
  </div>
  </div>
  

          )}
        </div>
      </div>
    </div>
  );
};

export default FacturationModal;